// A saved SQL view: a query across one or more of this organization's tables.
import { properties } from "@repoflow/common/schema-fields";
import { tableId, TABLE_ID_RE, isValidTableId, RESERVED_TABLE_IDS } from "@common/schema/table/fields";

const { createdAt, updatedAt } = properties;
export { createdAt, updatedAt, TABLE_ID_RE, isValidTableId, RESERVED_TABLE_IDS };

export const orgId = { type: "string" };

// The same identifier rule as a table's, and for a weaker reason: nothing names
// a view from inside SQL today. It is the same rule anyway, because a view is
// the obvious thing to want to query from another view later, and having to
// change the id rule then would mean changing a range key.
export const viewId = {
  label: "Name",
  type: "string",
  pattern: TABLE_ID_RE.source,
  minLength: 1,
  maxLength: 64,
  default: "",
  description: "Lowercase letters, digits and underscores.",
};

export const title = { label: "Title", type: "string", minLength: 1 };

export const description = {
  label: "Description",
  type: "string",
  textarea: true,
  default: "",
};

export const tags = {
  label: "Tags",
  type: "array",
  items: { type: "string" },
  widget: "tags",
  default: [],
};

// The query.
//
// `widget: "sql"` names @repoflow/components/widgets/sql — a CodeMirror 6 editor
// over @repoflow/components/code, already built and already registered by
// pagews. Naming it here is the whole of what makes this field a code editor.
export const query = {
  label: "Query",
  type: "string",
  widget: "sql",
  default: "",
};

// WHICH TABLES THE QUERY READS, AND IT IS DERIVED RATHER THAN TYPED.
//
// The model parses the query and writes this list; no input shape carries it.
// That is what makes it worth checking against — the anonymous rows route serves
// a table only when the view naming it lists it, and a list a person could type
// would be a check on the person rather than on the query.
//
// See @common/code/sql/tables for the four AST shapes that escape a naive walk,
// one of which (`UNION`) is a read of a table the list would not have named.
export const tables = {
  label: "Tables",
  type: "array",
  items: { ...tableId, label: "Table" },
  default: [],
  readOnly: true,
};

// A view's bind parameters, as /Types/Param — the same declaration shape a
// table's columns use, so the form a person fills in before running a shared
// view is compiled by the same types-values machinery.
//
// They are BOUND, never interpolated: the query writes `?` and these say what
// the caller is asked for. The archive substituted `{{params.x}}` into the SQL
// text before parsing it, with the values coming off a public page's route.
export const params = {
  label: "Parameters",
  type: "array",
  items: { $ref: "/Types/Param" },
  index: "name",
  default: [],
};

// The storage twin, for the reason @common/schema/table/fields gives for
// storedColumns: datorm validates with raw jsonschema, which cannot evaluate the
// `resolver` keyword the /Types/Param config union is resolved by.
export const storedParams = {
  type: "array",
  items: { type: "object" },
  default: [],
};

export const status = { type: "string" };

// `shareToken` lands with the share link, and lives BESIDE this schema rather
// than in it — it keys a sparse GSI, and datorm writes every schema property on
// every save with absent as null, which DynamoDB refuses on an index key.
